import { handleAbortError } from 'serviceWorker/helpers/abortErrorHandler';
import { broadcastPullFailed, type PullingContract } from 'serviceWorker/helpers/broadcastPullError';
import type { DataScope } from 'serviceWorker/helpers/dataScope';
import { EntityOperation, logEntityOperationFailure } from 'serviceWorker/helpers/entityFailureLog';
import type { Services } from 'shared/domain/messages/message';
import { debugLog } from 'shared/logger/debugLog';
import type { FailedPull } from './types';

interface PullFailure {
  error: unknown;
  abortController: AbortController;
  entityName: Services;
  projectId: string | undefined;
  channelContract: PullingContract;
  /** The scope of the pull: a failure of a pull for outdated data tells nothing. */
  scope: DataScope;
  /** Releases the downloading flag, all an aborted download needs. */
  stopDownloading: () => Promise<void>;
  /** Drops what the failed download stored and marks the entity as not downloaded. */
  resetStoredData: () => Promise<void>;
}

/**
 * Ends a download that failed, the same way for every kind of pull: an aborted
 * one (project switch, logout) failed at nothing and only releases its flag;
 * any other is logged, its data reset, `pullFailed` told, and it is returned
 * to the pull handler, which waits before asking again.
 */
export async function handlePullFailure({
  error,
  abortController,
  entityName,
  projectId,
  channelContract,
  scope,
  stopDownloading,
  resetStoredData,
}: PullFailure): Promise<void | FailedPull> {
  if (handleAbortError(error, abortController, `fetching ${entityName}`)) {
    debugLog(`Resetting isDownloading after abort for ${entityName}`);
    await stopDownloading();
    return;
  }

  logEntityOperationFailure(EntityOperation.FETCHING, { entityName, projectId, error });
  try {
    await resetStoredData();
  } finally {
    // Lets listeners waiting for `hasAll` stop loading. Skipped for a
    // pull started for data that is outdated now.
    await scope.write(() => broadcastPullFailed({ channelContract, entityName, error, projectId }));
  }
  return { error };
}
